import 'leaflet/dist/leaflet.css';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { FlightsWithVersionInfos } from '../models/flightswithversioninfos.model';
import manifestV1Service from '../services/manifest-v1.service';
import './ground-op-view.component.css';
import { FlightWithVersion } from '../models/flightwithversion.model';
import moment from 'moment';
import { FlightLoad } from '../models/flightload.model';
import { Flight } from '../models/flight.model';
import { Plane } from '../models/plane.model';
import '../shared/constants';
import Constants from '../shared/constants';
import { WorkType } from '../models/worktype.model';
import { Person } from '../models/person.model';
import { popupConfirm } from '../shared/confirm';
import { UpdateFlightsRequest } from '../models/updateflightsrequest.model';

interface GroundOpViewProps {
  editMode: boolean;
}

function GroundOpView(props: GroundOpViewProps) {
  const firstUpdate = useRef(true);
  const [planes, setPlanes] = useState<Plane[]>([]);
  const [workTypes, setWorkTypes] = useState<WorkType[]>([]);
  const [model, setModel] = useState<FlightWithVersion[]>([]);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [error, setError] = useState(false);
  const [editingField, setEditingField] = useState(false);
  const pilots = useRef<Person[]>([]);
  const teams = useRef<Person[]>([]);
  const jumpMasters = useRef<Person[]>([]);
  const loadDataTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const printingRef = useRef<boolean>(false);
  const downloadingRef = useRef<boolean>(false);


  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      loadData();
    }

    return () => {
      if (loadDataTimeoutRef.current != null) {
        clearTimeout(loadDataTimeoutRef.current);
      }
    };
  }, []);


  function isEditingData() {
    return editingField == true;
  }


  async function loadData() {
    // Set up an interval to call loadData() every 10 seconds
    if (loadDataTimeoutRef.current != null) {
      clearTimeout(loadDataTimeoutRef.current);
    }

    if (isEditingData()) {
      return;
    }

    // Load pilots, jump masters, teams once
    if (pilots.current.length == 0) {
      pilots.current = await manifestV1Service.getPilots();
    }
    if (teams.current.length == 0) {
      teams.current = await manifestV1Service.getTeams();
    }
    if (jumpMasters.current.length == 0) {
      jumpMasters.current = await manifestV1Service.getJumpMasters();
    }

    if (error) {
      return;
    }
    try {
      const data = await manifestV1Service.getFlights();
      if (isEditingData()) {
        return;
      }
      setPlanes(data.planes != null ? data.planes : []);
      setWorkTypes(data.workTypes != null ? data.workTypes : []);

      const filtered = filterSortData(data);
      setModel(filtered);
      setError(false);
    } catch (err) {
      setErrorMsg('' + err);
      setError(true);
    }

    loadDataTimeoutRef.current = setTimeout(loadData, 10000);
  }

  function getPilotName(id: number): string {
    for (let i = 0; i < pilots.current.length; i++) {
      if (pilots.current[i].id == id) {
        return pilots.current[i].firstName + " " + pilots.current[i].lastName;
      }
    }
    return "" + id;
  }

  function getTeamName(id: number): string {
    for (let i = 0; i < teams.current.length; i++) {
      if (teams.current[i].id == id) {
        return "" + teams.current[i].firstName;
      }
    }
    return "" + id;
  }

  function filterSortData(data: FlightsWithVersionInfos): FlightWithVersion[] {
    // for each plane: keep last departed + 2 next scheduled
    const perPlane: any = {};
    data?.flights?.forEach(f => {
      if (f.flight != null && f.flight.planeId != null && f.flight.departureTime != null) {
        let found = perPlane[f.flight.planeId];
        if (found == null) {
          found = {};
          found.lastTookOff = f.flight.status === Constants.DEPARTED ? f : null;
          found.nextScheduled = [];
          if (f.flight.status === Constants.SCHEDULED || f.flight.status === Constants.STAND_BY) {
            found.nextScheduled.push(f);
          }
          perPlane[f.flight.planeId] = found;
        } else {
          if (f.flight.status === Constants.SCHEDULED || f.flight.status === Constants.STAND_BY) {
            found.nextScheduled.push(f);
          }
          else if (f.flight.status === Constants.DEPARTED) {
            if (found.lastTookOff == null) {
              found.lastTookOff = f;
            } else {
              if (new Date(f.flight.departureTime).getTime() > new Date(found.lastTookOff.flight.departureTime).getTime()) {
                found.lastTookOff = f;
              }
            }
          }
        }
      }
    });

    let filtered: FlightWithVersion[] = [];
    for (const planeId in perPlane) {
      if (perPlane[planeId].lastTookOff != null) {
        filtered.push(perPlane[planeId].lastTookOff);
      }

      if (perPlane[planeId].nextScheduled) {
        perPlane[planeId].nextScheduled.sort((f1: Flight, f2: Flight) => {
          return (f1.stickNo ? f1.stickNo : 0) - (f2.stickNo ? f2.stickNo : 0);
        });
        filtered = filtered.concat(perPlane[planeId].nextScheduled);
      }
    }

    // Sort on departure time
    filtered.sort((fwv1, fwv2) => {
      if (fwv1.flight == null || fwv1.flight.departureTime == null) {
        return 0;
      }
      if (fwv2.flight == null || fwv2.flight.departureTime == null) {
        return 0;
      }
      return new Date(fwv1.flight.departureTime).getTime() - new Date(fwv2.flight?.departureTime).getTime();
    });

    // Mark jump masters & B2B
    filtered.forEach(fwv => {
      if (fwv.flight && fwv.flight.flightLoadsSorted) {
        fwv.flight.flightLoadsSorted.forEach(fl => {
          if (fwv.flight?.jumpMasterId === fl.personId) {
            fl.isJumpMaster = true;
          } else {
            if (inJumpMasterList(fl)) {
              fl.canBeJumpMaster = true;
            } else {
              fl.canBeJumpMaster = false;
            }
          }
          fl.b2b = isBackToBack(fl, fwv.flight as Flight, data != null && data.flights != null ? data.flights : []);
        });
      }
    });

    return filtered;
  }

  function isBackToBack(target: FlightLoad, f: Flight, flights: FlightWithVersion[]) {
    if (target.personId == null || target.personId === 0) {
      return false;
    }
    const previousFlightStickNo = (f.stickNo ? f.stickNo : 0) - 1;
    const previousFlightPlane = f.planeId;
    const previousFlight = flights.find(fwv => {
      if (fwv.flight && fwv.flight.flightLoadsSorted) {
        if (fwv.flight.stickNo === previousFlightStickNo && fwv.flight.planeId === previousFlightPlane) {
          return true;
        }
      }
      return false;
    });

    if (previousFlight) {
      if (previousFlight.flight?.refuelAfterLoad === true) {
        return false;
      }
      const found = previousFlight.flight?.flightLoadsSorted?.filter(fl => {
        return fl.personId === target.personId;
      });
      return found && found.length > 0;
    }
    return false;
  }

  function inJumpMasterList(fl: FlightLoad): boolean {
    if (fl.personId == null || fl.personId == 0) {
      return false;
    }
    for (let jmIdx = 0; jmIdx < jumpMasters.current.length; jmIdx++) {
      if (jumpMasters.current[jmIdx].id == fl.personId) {
        return true;
      }
    }
    return false;
  }

  async function takeOff(f: Flight | undefined) {
    if (f == null) {
      return;
    }
    if (f.status === Constants.SCHEDULED) {
      const confirmation = await popupConfirm(f.planeId + ' N°' + f.stickNo + ' departed ?', {
        okLabel: 'Yes',
        cancelLabel: 'No',
        title: 'Take off !'
      });

      if(confirmation.ok) {
        setSaving(true);
        const ufreq: UpdateFlightsRequest = new UpdateFlightsRequest();
        ufreq.targetFlightId = f.flightId;
        ufreq.setTookOff = true;

        const ufres = await manifestV1Service.updateFlights(ufreq);

        if (ufres.updated == false) {
          popupConfirm('Error while updating status !', {
            okLabel: 'OK',
            title: 'Error'
          });
          setSaving(false);
        } else {
          loadData();
          setSaving(false);
        }
      }
    }
  }

  async function standBy(f: Flight | undefined) {
    if (f == null) {
      return;
    }
    if (f.status === Constants.SCHEDULED) {
      const confirmation = await popupConfirm(f.planeId + ' N°' + f.stickNo + ' : Stand By ?', {
        okLabel: 'Yes',
        cancelLabel: 'No',
        title: 'Stand By'
      });

      if(confirmation.ok) {
        setSaving(true);
        const ufreq: UpdateFlightsRequest = new UpdateFlightsRequest();
        ufreq.targetFlightId = f.flightId;
        ufreq.setStandBy = true;

        const ufres = await manifestV1Service.updateFlights(ufreq);

        if (ufres.updated == false) {

          popupConfirm('Error while setting stand by !', {
            okLabel: 'OK',
            title: 'Error'
          });
          setSaving(false);
        } else {
          loadData();
          setSaving(false);
        }
      }
    }
  }

  async function reschedule(f: Flight | undefined) {
    if (f == null) {
      return;
    }
    if (f.status === Constants.DEPARTED || f.status === Constants.STAND_BY) {
      const confirmation = await popupConfirm('Re-schedule ' + f.planeId + ' N°' + f.stickNo + ' ?', {
        okLabel: 'Yes',
        cancelLabel: 'No',
        title: 'Re-schedule'
      });

      if (confirmation.ok) {
        setSaving(true);
        const ufreq: UpdateFlightsRequest = new UpdateFlightsRequest();
        ufreq.targetFlightId = f.flightId;
        ufreq.setReschedule = true;

        const ufres = await manifestV1Service.updateFlights(ufreq);

        if (ufres.updated == false) {

          popupConfirm('Error while rescheduling !', {
            okLabel: 'OK',
            title: 'Error'
          });
          setSaving(false);
        } else {
          loadData();
          setSaving(false);
        }
      }
    }
  }


  async function changeDeparture(f: Flight | undefined, nowPlusMin: number) {
    if (f == null) {
      return;
    }
    if (f.status === Constants.SCHEDULED) {
      const target = new Date();
      target.setTime(target.getTime() + (nowPlusMin * 60 * 1000) + 30000); // 30 sec added for Suzy
      const targetStr = moment(target).format('HH:mm')

      const confirmation = await popupConfirm('Set departure time of ' + f.planeId + ' N°' + f.stickNo + ' on ' + targetStr + '  ?', {
        okLabel: 'Yes',
        cancelLabel: 'No',
        title: 'Departure time'
      });

      if (confirmation.ok) {
        setSaving(true);
        const ufreq: UpdateFlightsRequest = new UpdateFlightsRequest();
        ufreq.targetFlightId = f.flightId;
        ufreq.newDepartureTime = target;

        const ufres = await manifestV1Service.updateFlights(ufreq);
        if (ufres.updated == false) {

          popupConfirm('Error while rescheduling !', {
            okLabel: 'OK',
            title: 'Error'
          });
          setSaving(false);
        } else {
          loadData();
          setSaving(false);
        }
      }
    }
  }


  async function setJumpMaster(fl: FlightLoad, f: Flight | undefined) {
    if (f == null) {
      return;
    }
    if (fl.canBeJumpMaster && f.status === Constants.SCHEDULED) {
      const response = await popupConfirm('Set ' + fl.personName + ' as jump master ?', {
        okLabel: 'Yes',
        cancelLabel: 'No',
        title: 'Jump Master for ' + f.planeId + ' N°' + f.stickNo
      })

      if (response.ok) {
        setSaving(true);
        const ufreq: UpdateFlightsRequest = new UpdateFlightsRequest();
        ufreq.targetFlightId = f?.flightId;
        ufreq.newJumpMaster = fl.personId;

        const ufres = await manifestV1Service.updateFlights(ufreq);

        if (ufres.updated == false) {

          popupConfirm('Error while setting Jump Master !', {
            okLabel: 'OK',
            title: 'Error'
          });
          setSaving(false);
        } else {
          loadData();
          setSaving(false);
        }
      }
    }
  }

  async function call(f: Flight) {
    const response = await popupConfirm('Text to read : ',
      {
        okLabel: 'OK',
        cancelLabel: 'Cancel',
        title: 'Voice call',
        inputText: 'Attention please. Boarding now. ' + f.planeId + ' number ' + f.stickNo + '. Boarding now.'
      });
    if (response.ok && response.text != null && response.text.trim() != '') {
      manifestV1Service.doCall(response.text);
    }
  }

  async function printWeightAndBalance(f: Flight, nb: number) {
    if (printingRef.current) {
      return;
    }
    printingRef.current = true;
    if (f == null) {
      return;
    }

    try {
      await manifestV1Service.printWeightAndBalance(f.flightId, nb);
      popupConfirm('Documents printed : ' + nb, {
        okLabel: 'OK',
        title: 'Flight Balance'
      });
    } catch (err) {
      setErrorMsg('' + err);
      setError(true);
    }
    printingRef.current = false;
  }

  async function downloadWeightAndBalance(f: Flight) {
    if (downloadingRef.current) {
      return;
    }
    downloadingRef.current = true;
    if (f == null) {
      return;
    }

    try {
      await manifestV1Service.downloadWeightAndBalance(f.flightId);
    } catch (err) {
      setErrorMsg('' + err);
      setError(true);
    }

    downloadingRef.current = false;
  }

  function getPersonRowColor(fl: FlightLoad, f: Flight | undefined, workTypes: WorkType[]): string {
    if (f == null) {
      return "#FFFFFF";
    }
    /*
    if(f.status!=Constants.SCHEDULED) {
      return "#FFFFFF";
    }*/

    if (fl != null) {
      if (fl.personName == Constants.AVAILABLE) {
        return "#444444";
      }
    }
    if (f != null && fl.workType != null) {
      for (let i = 0; i < workTypes.length; i++) {
        if (workTypes[i].code === fl.workType) {
          const col = workTypes[i].color;
          if (col == null || col == "0") {
            return "#FFFFFF";
          }
          let hexCol = parseInt(col).toString(16);
          while (hexCol.length < 6) {
            hexCol = '0' + hexCol;
          }
          return "#" + hexCol;
        }
      }
    }
    return "#FFFFFF";
  }

  function getJumpShortLib(f: FlightLoad, workTypes: WorkType[]): string {
    if (f.teamId != 0) {
      return "";
    }

    if (f != null && f.workType != null) {
      for (let i = 0; i < workTypes.length; i++) {
        if (workTypes[i].code === f.workType) {
          return workTypes[i].shortLib != null && workTypes[i].shortLib != "" ? workTypes[i].shortLib + " " : "";
        }
      }
    }
    return "";
  }

  /***
   * Regroup attached loads
   */
  function getMarginTop(f: FlightLoad, seatNo: number): string {
    if (seatNo == 1) {
      return '0px';
    }
    if (f != null) {
      if (f.rootFlightLoad != null && f.rootFlightLoad != 0) {
        return '0px';
      } else {
        return '12px';
      }
    }
    return '12px';
  }

  function getPersonName(l: FlightLoad, flight: Flight | undefined): String {
    if (flight == null) {
      return "";
    }
    if (flight.autoFillValidated == false && (
      l.autofillStatus == Constants.AUTO_FILL_DONE ||
      l.autofillStatus == Constants.AUTO_FILL_POPUP_CONFIRMED
    )
    ) {
      return "";
    }
    if (l.teamId == 0) {
      return l.personName != null ? l.personName : "";
    } else {
      return (l.personName != null ? l.personName : "") + " [" + getTeamName(l.teamId != null ? l.teamId : 0) + "]";
    }
  }

  function getPersonsBgColor(f: Flight, planes: Plane[]): string {
    if (f.status == Constants.DEPARTED) {
      return Constants.DEPARTED_COLOR_LIST;
    }

    if (f.status == Constants.STAND_BY) {
      return Constants.STAND_BY_COLOR_LIST;
    }

    return "#000000";
  }

  function getBgColor(f: Flight, planes: Plane[]): string {
    if (f.status == Constants.DEPARTED) {
      return Constants.DEPARTED_COLOR;
    }

    if (f.status == Constants.STAND_BY) {
      return Constants.STAND_BY_COLOR;
    }

    let plane: Plane = new Plane();
    plane.planeColor = "0";
    planes.forEach(p => {
      if (p.planeId === f.planeId) {
        plane = p;
      }
    });

    return "#" + parseInt(plane.planeColor).toString(16);
  }

  function getTimeStatusLib(f: Flight): string {
    if (f.status == Constants.STAND_BY) {
      return "";
    }
    if (f.departureTime == null) {
      return "";
    }

    const now = new Date();
    const diffMs = new Date(f.departureTime).getTime() - now.getTime();
    const diffMins = Math.round(diffMs / 1000 / 60);

    if (diffMins > 0) {
      if (f.status != Constants.DEPARTED) {
        return diffMins + " min remaining";
      }
      return ""; // should not happen
    } else {
      if (f.status == Constants.DEPARTED) {
        return Math.abs(diffMins) + " min ago";
      } else {
        return Math.abs(diffMins) + " min late";
      }
    }
  }

  function getColumnFooter1(f: Flight, flights: FlightWithVersion[]): string {
    if (f == null) {
      return "";
    }

    // Refuel 
    let refuelInPreviousFlight = false;
    const stickNo = f.stickNo != null ? f.stickNo : -1;

    flights.forEach(fl => {
      if (fl.flight != null) {
        if (fl.flight.planeId == f.planeId) {
          if (fl.flight.refuelAfterLoad == true && fl.flight.stickNo == stickNo - 1) {
            refuelInPreviousFlight = true;
          }
        }
      }
    });

    if (refuelInPreviousFlight) {
      return "REFUEL BEFORE";
    }
    return "";
  }

  /***
   * Flight Footer label2
   * 
   */
  function getColumnFooter2(f: Flight): string {
    if (f == null) {
      return "";
    }

    var label: string = '';
    if (f.planeIsFull == true && f.status != Constants.DEPARTED) {
      label = "NO MORE PLACE";
    }
    return label;
  }

  /***
   * Flight Footer label2
   * 
   */
  function getColumnFooter3(f: Flight): string {
    if (f == null) {
      return "";
    }

    if (f.minJumps != null && f.minJumps > 0) {
      return "MIN " + f.minJumps + " JUMPS";
    }
    return "";
  }

  function getActionButtons(f: Flight): ReactElement | null {
    if (f.status == Constants.STAND_BY) {
      return <div><div>STAND BY</div>
        <input type="button" value={"Re-schedule !"} className='took-off-button' onClick={() => reschedule(f)} />
      </div>
    }
    if (f.status == Constants.DEPARTED) {
      return <div><div>DEPARTED</div>
        <input type="button" value={"Re-schedule !"} className='took-off-button' onClick={() => reschedule(f)} />
        <input type="button" value={"Download W&B"} className='scheduled-button' onClick={() => downloadWeightAndBalance(f)} />
      </div>
    }
    if (f.status == Constants.SCHEDULED) {
      return <>
        <input type="button" value={"Now +5"} className='scheduled-button' onClick={() => changeDeparture(f, 5)} />
        <input type="button" value={"Now +10"} className='scheduled-button' onClick={() => changeDeparture(f, 10)} />
        <input type="button" value={"Now +15"} className='scheduled-button' onClick={() => changeDeparture(f, 15)} />
        <input type="button" value={"Now +20"} className='scheduled-button' onClick={() => changeDeparture(f, 20)} />
        <input type="button" value={"Now +30"} className='scheduled-button' onClick={() => changeDeparture(f, 30)} />
        <div style={{ marginTop: '8px' }}></div>
        <input type="button" value={"Print !"} className='scheduled-button' onClick={() => printWeightAndBalance(f, 3)} />
        <input type="button" value={"Download W&B"} className='scheduled-button' onClick={() => downloadWeightAndBalance(f)} />
        <input type="button" value={"Take off !"} className='scheduled-button' onClick={() => takeOff(f)} />
        <input type="button" value={"Stand by !"} className='scheduled-button' onClick={() => standBy(f)} />
        <input type="button" value={"Call !"} className='scheduled-button' onClick={() => call(f)} />
      </>
    }
    return null;
  }

  function refresh() {
    window.location.reload();
  }

  function getFillGroupName(fillGroup: number) {
    if (fillGroup == null) {
      return "";
    }
    if (fillGroup === 0x000000) {
      return ""; // (inverted)
    }
    if (fillGroup === 0xFFFFFF) {
      return "B0"; // (inverted)
    }
    if (fillGroup === 0xFF0000) {
      return "R1";
    }
    if (fillGroup === 0x00FF00) {
      return "G1";
    }
    if (fillGroup === 0x0000FF) {
      return "B2";
    }
    if (fillGroup === 0x3c3c3c) {
      return "G2";
    }
    if (fillGroup === 0xFF00FF) {
      return "P1";
    }
    if (fillGroup === 0x00FFFF) {
      return "B3";
    }
    if (fillGroup === 0xFFFF00) {
      return "Y1";
    }
    if (fillGroup === 0x3F4CDD) {
      return "B1";
    }
    if (fillGroup === 0x6600AA) {
      return "P2";
    }
    return "X" + (fillGroup % 10) + "";
  }

  return (
    error ? <div style={{ color: "red", fontSize: "20px", textAlign: "center" }}>
      <div className="red-message-top">{errorMsg}</div>
      <input type="button"
        className="open-close-dropzone-button"
        onClick={refresh}
        value={"Reload page"} />
    </div>
      :
      <>
        {saving ?
          <div className="saving-div">
            <div className="saving-text">Saving</div>
            <div className="saving-loader">
              <span className="loader__element"></span>
              <span className="loader__element"></span>
              <span className="loader__element"></span>
            </div>
          </div> : null}
        <div className='gop-flights-view'>
          <div className="gop-flights-list" >
            {model?.map((fwv: FlightWithVersion) => (
              <div className="gop-flight">
                {fwv.flight != null ?
                  <>
                    <div className="gop-flight-header" style={{ backgroundColor: getBgColor(fwv.flight, planes) }}>
                      <div>
                        {fwv.flight.planeId} N°{fwv.flight.stickNo} - {moment(fwv.flight.departureTime).format('HH:mm')}
                      </div>
                      <div>
                        {getTimeStatusLib(fwv.flight)}
                      </div>
                      <div style={{ borderTop: '1px solid white', marginTop: '3px', marginBottom: '3px', fontSize: '15px' }}>
                        <div>{fwv.flight.pilotId != null && fwv.flight.pilotId != 0 ? getPilotName(fwv.flight.pilotId) : ""}</div>
                        <div>{fwv.flight.copilotId != null && fwv.flight.copilotId != 0 ? getPilotName(fwv.flight.copilotId) : ""} </div>
                      </div>

                    </div>

                    <div className="gop-flight-persons" style={{ backgroundColor: getPersonsBgColor(fwv.flight, planes) }}>
                      {fwv.flight.flightLoadsSorted?.map((fl: FlightLoad, seatNo: number, arr: FlightLoad[]) => (
                        <div style={{
                          fontSize: '14px',
                          marginTop: getMarginTop(fl, seatNo + 1),
                          padding: '2px',
                          color: getPersonRowColor(fl, fwv.flight, workTypes),
                          backgroundColor: fl.isJumpMaster == true ? "rgb(197 91 243 / 50%)" : "transparent",
                          cursor: fl.canBeJumpMaster ? 'pointer' : '',
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'space-between'
                        }}
                          onClick={() => setJumpMaster(fl, fwv.flight)}
                        >
                          <div>{seatNo + 1}.
                            {getPersonName(fl, fwv.flight)}
                            {((!fl.flightLoads || fl.flightLoads.length === 0) && !fl.rootFlightLoad && fl.fillGroup) ? <span className={"gop-fill-group gop-fill-group-" + fl.fillGroup.toString(16).padStart(6, '0').toUpperCase()}>
                              {getFillGroupName(fl.fillGroup)}
                            </span> : null}
                            {fl.canBeJumpMaster ? <span style={{ color: 'rgb(197 91 243)', marginLeft: '3px' }}>*</span> : null}
                            {fl.b2b ? <span style={{ color: 'rgb(253 0 255)', marginLeft: '3px' }}>B2B</span> : null}
                          </div>

                          <div>{getJumpShortLib(fl, workTypes)}</div>
                        </div>
                      ))}
                    </div>

                    <div className="gop-flight-footer" style={{ backgroundColor: getBgColor(fwv.flight, planes) }}>
                      <div>
                        {getColumnFooter1(fwv.flight, model)}
                      </div>
                      <div>
                        {getColumnFooter2(fwv.flight)}
                      </div>
                      <div>
                        {getColumnFooter3(fwv.flight)}
                      </div>
                      <div>
                        {getActionButtons(fwv.flight)}
                      </div>
                    </div>
                  </>
                  : null}

              </div>
            ))}
          </div>
        </div>
      </>
  )
}

export default GroundOpView; 
